import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const INACTIVITY_MS = 15 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("cm_user");
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(false);

  const logout = useCallback(async () => {
    // Tente d'effacer le cookie httpOnly côté serveur (idempotent)
    try { await api.post("/auth/logout"); } catch { /* silencieux : on déconnecte quand même côté client */ }
    localStorage.removeItem("cm_token");
    localStorage.removeItem("cm_user");
    setUser(null);
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }, []);

  const login = useCallback(async (login_, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { login: login_, password });
      localStorage.setItem("cm_token", data.token);
      localStorage.setItem("cm_user", JSON.stringify(data.user));
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || "Erreur de connexion" };
    } finally {
      setLoading(false);
    }
  }, []);

  // Inactivity timeout
  useEffect(() => {
    if (!user) return;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => logout(), INACTIVITY_MS);
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  // Mémoïsé pour éviter de re-render tous les consommateurs à chaque render parent
  const value = useMemo(
    () => ({ user, login, logout, loading }),
    [user, login, logout, loading],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
